import { AxiosResponse } from "axios";
import { IOptions } from "../../api-sdk";
import BaseService from "../baseService";
import {
  IHistory,
  IHistoryDelete,
  IHistoryDeleteResponse,
  IHistoryUpdate,
  IHistoryUpdateV1,
  IHistoryUpdateV1Response,
  IHistoryV1,
  IHistoryV1Query,
} from "./history.types";
import { IContentData } from "../content/content.types";
import { IPaginatedResponse } from "../../../types";
import { filterNonNull } from "../../../utils";
import qs from "qs";

class HistoryService extends BaseService {
  constructor(options: IOptions) {
    super(options);
  }

  /**
   * Create or update a watch-history entry via `POST /api/v1/history`.
   *
   * Tracks the user's playback position for a piece of content.
   * If a history row already exists for the given content `id`, it is updated;
   * otherwise a new row is created.
   *
   * @param payload - History update payload.
   * @param payload.id - Content ID to track.
   * @param payload.type - `"movie"` or `"series"`.
   * @param payload.currentTime - Current playback position in seconds.
   * @param payload.duration - Total duration of the content in seconds.
   * @param payload.current_season - Season ID (series only).
   * @param payload.current_episode - Episode ID (series only).
   * @param payload.season_number - Season number (series only).
   * @param payload.episode_number - Episode number (series only).
   * @returns `{ id, message }` confirming the upsert.
   *
   * @example
   * const res = await zott.history.updateV1({
   *   id: "content_abc",
   *   type: "movie",
   *   currentTime: 300,
   *   duration: 7200,
   * });
   * console.log(res.data.message); // "History updated"
   */
  async updateV1(payload: IHistoryUpdateV1): Promise<AxiosResponse<IHistoryUpdateV1Response>> {
    return this.request({
      method: "POST",
      url: "/api/v1/history",
      data: payload,
    });
  }

  /**
   * Fetch the authenticated user's watch history via `GET /api/v1/history`.
   *
   * Returns a paginated list of history entries. Supports pagination, sorting,
   * search, field selection (`select`), filters, and deep population (`populate`).
   *
   * Pass generic `H` / `C` when `query.select` or populate `select` narrows
   * fields — see {@link IHistoryV1}.
   *
   * @typeParam H - Custom history row shape when `select` returns a subset. Default uses full {@link IHistoryV1Fields}.
   * @typeParam C - Custom populated `content` shape when populate `select` narrows fields. Default {@link IContentData}.
   * @param query - Optional query parameters for pagination, sorting, filtering, and population.
   * @returns Paginated response of {@link IHistoryV1} items.
   *
   * @example
   * // Basic paginated fetch
   * const res = await zott.history.getList({ page: 1, limit: 20 });
   *
   * @example
   * // With select, filters, and populate
   * const res = await zott.history.getList({
   *   page: 1,
   *   limit: 10,
   *   select: "content currentTime duration",
   *   filters: { completed: true },
   *   populate: [{ path: "content", select: "name slug poster" }],
   * });
   */
  async getList<H = void, C = IContentData>(
    query?: IHistoryV1Query
  ): Promise<AxiosResponse<IPaginatedResponse<IHistoryV1<H, C>>>> {
    return this.request({
      method: "GET",
      url: `/api/v1/history${query ? `?${qs.stringify(filterNonNull(query))}` : ""}`,
    });
  }

  /**
   * Delete one or more history entries via `DELETE /api/v1/history`.
   *
   * @param payload - Object containing an array of history entry IDs to remove.
   * @param payload.ids - Array of history `_id` values.
   * @returns `{ message, deletedCount }` confirming the deletion.
   *
   * @example
   * const res = await zott.history.delete({ ids: ["id_1", "id_2"] });
   * console.log(res.data.deletedCount); // 2
   */
  async delete(payload: IHistoryDelete): Promise<AxiosResponse<IHistoryDeleteResponse>> {
    return this.request({
      method: "DELETE",
      url: "/api/v1/history",
      data: payload,
    });
  }

  /**
   * Fetch all users' watch history for admin management via `GET /api/v1/history/manage`.
   *
   * Same query interface as {@link getList} but returns history across all users.
   * Requires admin-level authentication.
   *
   * @typeParam H - Custom history row shape when `select` returns a subset.
   * @typeParam C - Custom populated `content` shape. Default {@link IContentData}.
   * @param query - Optional query parameters for pagination, sorting, filtering, and population.
   * @returns Paginated response of {@link IHistoryV1} items.
   *
   * @example
   * const res = await zott.history.getListManage({
   *   page: 1,
   *   limit: 50,
   *   order: "desc",
   *   sortBy: "updatedAt",
   *   populate: [{ path: "content", select: "name slug" }],
   * });
   */
  async getListManage<H = void, C = IContentData>(
    query?: IHistoryV1Query
  ): Promise<AxiosResponse<IPaginatedResponse<IHistoryV1<H, C>>>> {
    return this.request({
      method: "GET",
      url: `/api/v1/history/manage${query ? `?${qs.stringify(filterNonNull(query))}` : ""}`,
    });
  }

  /**
   * Fetches the history data from the server.
   *
   * @param query Optional query string for filtering (e.g., "?user=123")
   * @returns AxiosResponse with IHistory data
   * @deprecated Use {@link getList} instead.
   */
  async get(query?: string): Promise<AxiosResponse<IHistory>> {
    return this.request({
      method: "GET",
      url: query ? `api/history${query}` : "api/history",
    });
  }

  /**
   * Updates a history entry on the server.
   *
   * @param payload The history update payload
   * @returns AxiosResponse with updated IHistory data
   * @deprecated Use {@link updateV1} instead.
   */
  async update(payload: IHistoryUpdate): Promise<AxiosResponse<IHistory>> {
    return this.request({
      method: "PUT",
      url: "api/history",
      data: payload,
    });
  }
}

export default HistoryService;
